// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type {
  Composition,
  Condition,
  DiagnosticReport,
  DocumentReference,
  MedicationRequest,
  MedicationStatement,
  Patient,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  CLOUDPAT_HSAT_CODING,
  COMPOSITION_TYPE_PCP_REFERRAL_INTAKE,
  COMPOSITION_TYPE_SELF_REFERRAL_INTAKE,
  COMPOSITION_TYPE_SYSTEM,
} from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

const INTAKE_COMPOSITION_TYPES = [
  `${COMPOSITION_TYPE_SYSTEM}|${COMPOSITION_TYPE_SELF_REFERRAL_INTAKE}`,
  `${COMPOSITION_TYPE_SYSTEM}|${COMPOSITION_TYPE_PCP_REFERRAL_INTAKE}`,
].join(',');
const HSAT_SR_CODE_FILTER = `${CLOUDPAT_HSAT_CODING.system}|${CLOUDPAT_HSAT_CODING.code}`;

function waitForPrescriptionDocumentRetry(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 1500));
}

export interface MyChartData {
  intakeComposition: Composition | undefined;
  conditions: Condition[];
  prescriptions: MedicationRequest[];
  prescriptionDocuments: DocumentReference[];
  medications: MedicationStatement[];
  hsatOrders: ServiceRequest[];
  hsatReports: DiagnosticReport[];
  loading: boolean;
  error: Error | null;
  setMedications: (meds: MedicationStatement[]) => void;
  refresh: () => void;
}

/**
 * Aggregates all read-only patient-chart data for the signed-in patient:
 * intake Composition, Conditions, MedicationRequests (prescriptions),
 * MedicationStatements (self-reported), HSAT ServiceRequests, and any
 * HSAT DiagnosticReports linked to those orders via `basedOn`.
 */
export function useMyChartData(): MyChartData {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;

  const [intakeComposition, setComposition] = useState<Composition | undefined>();
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [prescriptions, setPrescriptions] = useState<MedicationRequest[]>([]);
  const [prescriptionDocuments, setPrescriptionDocuments] = useState<DocumentReference[]>([]);
  const [medications, setMedicationsState] = useState<MedicationStatement[]>([]);
  const [hsatOrders, setHsatOrders] = useState<ServiceRequest[]>([]);
  const [hsatReports, setHsatReports] = useState<DiagnosticReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const patientRef = getReferenceString(profile);
      const [comp, conditionResults, rxResults, medResults, hsatResults, documentResults] = await Promise.all([
        medplum
          .searchOne('Composition', {
            subject: patientRef,
            type: INTAKE_COMPOSITION_TYPES,
            _sort: '-date',
          })
          .catch(() => undefined),
        medplum
          .searchResources('Condition', {
            subject: patientRef,
            _sort: '-recorded-date',
            _count: '100',
          })
          .catch(() => []),
        medplum
          .searchResources('MedicationRequest', `patient=${patientRef}&intent=order&_sort=-authoredon&_count=100`, {
            cache: 'no-cache',
          })
          .catch(() => []),
        medplum
          .searchResources('MedicationStatement', {
            subject: patientRef,
            _sort: '-_lastUpdated',
            _count: '100',
          })
          .catch(() => []),
        medplum
          .searchResources('ServiceRequest', {
            subject: patientRef,
            code: HSAT_SR_CODE_FILTER,
            _count: '5',
          })
          .catch(() => []),
        medplum
          .searchResources('DocumentReference', {
            subject: patientRef,
            _sort: '-date',
            _count: '200',
          })
          .catch(() => []),
      ]);

      const hsatOrderList = Array.from(hsatResults) as ServiceRequest[];
      // Only fetch DiagnosticReports when there are orders to link them to —
      // otherwise the `based-on` search would have no input.
      const hsatReportList = hsatOrderList.length
        ? await medplum
            .searchResources('DiagnosticReport', {
              'based-on': hsatOrderList.map((sr) => `ServiceRequest/${sr.id}`).join(','),
              _count: '1000',
              _sort: '-_lastUpdated',
            })
            .then((results) => Array.from(results) as DiagnosticReport[])
            .catch(() => [] as DiagnosticReport[])
        : [];

      let resolvedDocumentResults = Array.from(documentResults) as DocumentReference[];
      let medicationRequests = Array.from(rxResults) as MedicationRequest[];
      if (medicationRequests.length === 0) {
        medicationRequests = (await medplum
          .searchResources('MedicationRequest', `patient=${patientRef}&_sort=-authoredon&_count=100`, {
            cache: 'no-cache',
          })
          .catch(() => [])) as MedicationRequest[];
      }
      const hasPrescriptionDocument = resolvedDocumentResults.some((document) =>
        document.context?.related?.some((reference) =>
          medicationRequests.some(
            (request) =>
              reference.reference === `MedicationRequest/${request.id}` ||
              reference.reference?.endsWith(`/MedicationRequest/${request.id}`)
          )
        )
      );
      if (medicationRequests.length > 0 && !hasPrescriptionDocument) {
        await waitForPrescriptionDocumentRetry();
        const relatedResults = await Promise.all(
          medicationRequests
            .filter((request): request is MedicationRequest & { id: string } => Boolean(request.id))
            .map((request) =>
              medplum
                .searchResources('DocumentReference', {
                  subject: patientRef,
                  related: `MedicationRequest/${request.id}`,
                  _sort: '-date',
                  _count: '20',
                })
                .catch(() => [])
            )
        );
        const mergedDocuments = [...resolvedDocumentResults, ...relatedResults.flat()] as DocumentReference[];
        resolvedDocumentResults = mergedDocuments.filter(
          (document, index) => mergedDocuments.findIndex((candidate) => candidate.id === document.id) === index
        );
      }

      setComposition(comp as Composition | undefined);
      setConditions(Array.from(conditionResults) as Condition[]);
      setPrescriptions(medicationRequests);
      setPrescriptionDocuments(resolvedDocumentResults);
      setMedicationsState(Array.from(medResults) as MedicationStatement[]);
      setHsatOrders(hsatOrderList);
      setHsatReports(hsatReportList);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load chart data'));
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  const setMedications = useCallback((meds: MedicationStatement[]) => {
    setMedicationsState(meds);
  }, []);

  return {
    intakeComposition,
    conditions,
    prescriptions,
    prescriptionDocuments,
    medications,
    hsatOrders,
    hsatReports,
    loading,
    error,
    setMedications,
    refresh: fetch,
  };
}
